<script lang="ts">
enum DrawerMenuKey {
  LAYOUTS = "layers",
  PAPER_SPACE = "paperSpace",
  BLOCKS = "blocks",
  SETTINGS = "settings",
  OBSERVATIONS = "observations",
}
</script>

<script setup lang="ts">
import { Z_INDEX_DRAWER } from "./constants";
import {
  canvasTopForTexts,
  effectiveDrawerWidth,
} from "@/stores/CanvasLayoutState";
import { isDrawerOpen } from "@/stores/DrawerWidthState";
import { isCanvasDrawing } from "@/stores/CanvasViewState";
import DRBtnIcon from "../../UI/DRBtnIcon.vue";
import DrawerTabBubbles, { type DrawerTab } from "./DrawerTabBubbles.vue";
import DrawerResizeHandle from "./DrawerResizeHandle.vue";
import LayersMenu from "../DrawerMenus/LayersMenu.vue";
import PaperSpaceMenu from "../DrawerMenus/PaperSpaceMenu.vue";
import SettingsMenu from "../DrawerMenus/SettingsMenu.vue";
import ObjectsMenu from "../DrawerMenus/objects/ObjectsMenu.vue";
import { isAssetsCategorySelected } from "@/composable/useObjectsCatalog";
import ObservationsDrawer from "../DrawerMenus/observations/ObservationsDrawer.vue";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { DraggerType } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { trackTab, type DrawerTabKey } from "@/analytics/drawing-analytics";

import {
  activeTab as observationsTab,
  isAnalysisTab,
  openRequested as observationsOpenRequested,
  consumeOpenRequest as consumeObservationsOpenRequest,
  setActiveTab as setObservationsTab,
} from "@/stores/ObservationsPanelState";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";

// Chaque onglet = une famille d'analytics (DRAWING_TAB_*). Ajouter, fusionner,
// renommer un onglet ou déplacer une action d'onglet change le regroupement de
// la page Usage du back-office : mettre à jour en même temps TabActions
// (analytics/drawing-analytics.ts), USAGE_FAMILIES (INSPEXIO-BACK
// stats/usage-families.ts) et DRAWER_TABS (DR-BACKOFFICE
// constants/usageBreakdowns.ts).
// La famille « analysis » n'a plus d'onglet à elle dans cette barre : c'est
// l'onglet IA du volet Observations qui l'alimente. Son « opened » reste un
// geste délibéré — ouvrir le volet sur cet onglet (ci-dessous) ou y entrer
// depuis un autre onglet du volet (ObservationsDrawerTabs) — le regroupement
// de la page Usage ne bouge pas. L'onglet Parties classe des observations :
// il n'en fait pas partie, et son ouverture n'est pas suivie.
const DRAWER_TABS: Record<DrawerMenuKey, DrawerTabKey> = {
  [DrawerMenuKey.LAYOUTS]: "layers",
  [DrawerMenuKey.PAPER_SPACE]: "paper_space",
  [DrawerMenuKey.BLOCKS]: "blocks",
  [DrawerMenuKey.SETTINGS]: "settings",
  [DrawerMenuKey.OBSERVATIONS]: "observations",
};

defineProps<{
  drawingId: number;
  fullscreen: boolean;
}>();

const activeDrawer = ref<DrawerMenuKey | null>(null);

// Partagé : le rappel de sauvegarde se cale à gauche du volet ouvert.
watch(activeDrawer, (key) => (isDrawerOpen.value = key !== null), {
  immediate: true,
});
onBeforeUnmount(() => (isDrawerOpen.value = false));

const planAnalysisState = usePlanAnalysisState();

// Activité et sauvegardes ne sont plus des onglets : elles s'ouvrent en
// modale depuis le menu fichier (header/CanvasFileMenu.vue). Les images sont
// devenues un outil de la barre d'outils (Tools/ImageToolMenu.vue). Même
// contenu et même suivi analytics (« opened » de leur famille) qu'en onglet.

interface DrawerMenuEntry {
  key: DrawerMenuKey;
  icon: string;
  component: any;
  highlight?: () => boolean;
}

const drawerMenuList: DrawerMenuEntry[] = [
  {
    key: DrawerMenuKey.LAYOUTS,
    icon: "stack",
    component: LayersMenu,
  },
  {
    key: DrawerMenuKey.PAPER_SPACE,
    icon: "article",
    component: PaperSpaceMenu,
  },
  // Objets : blocs (légende, dessin, Inspexio) et parties d'ouvrage — ex-onglets
  // Blocs et Ouvrages. Garde la clé « blocks » (famille analytics) ; l'ouverture
  // de la catégorie Parties d'ouvrage reste suivie dans la famille « assets ».
  {
    key: DrawerMenuKey.BLOCKS,
    icon: "cube",
    component: ObjectsMenu,
    highlight: () => DraggerMenuState.activeDragger.name == DraggerType.Block,
  },
  // Observations réelles + analyse IA et classement par partie (onglets internes)
  {
    key: DrawerMenuKey.OBSERVATIONS,
    icon: "binoculars",
    component: ObservationsDrawer,
  },
  // Toujours en dernier : les réglages restent l'onglet le plus bas de la barre
  {
    key: DrawerMenuKey.SETTINGS,
    icon: "sliders",
    component: SettingsMenu,
  },
];

const drawerTabs = computed<DrawerTab[]>(() =>
  drawerMenuList.map((menu) => ({
    key: menu.key,
    icon: menu.icon,
    highlighted: menu.highlight?.() ?? false,
  })),
);

/** Volet en cours de redimensionnement (les bulles le suivent sans animation). */
const isResizing = ref(false);

function handleCloseDrawer() {
  activeDrawer.value = null;
}

function toggleDrawer(key: DrawerMenuKey) {
  const isOpening = activeDrawer.value !== key;
  activeDrawer.value = isOpening ? key : null;
  // Every tab open lands in that tab's own analytics family (a close is
  // not a visit).
  if (isOpening) {
    trackTab(DRAWER_TABS[key], "opened");
    if (
      key === DrawerMenuKey.OBSERVATIONS &&
      planAnalysisState.hasAnalysis &&
      isAnalysisTab(observationsTab.value)
    ) {
      trackTab("analysis", "opened");
    }
    // Les parties d'ouvrage (ex-onglet Ouvrages), catégorie de l'onglet
    // Objets : l'ouvrir sur elles compte comme les ouvrir.
    if (key === DrawerMenuKey.BLOCKS && isAssetsCategorySelected()) {
      trackTab("assets", "opened");
    }
  }
}

// Ouverture du drawer Observations demandée ailleurs (menu de sélection) :
// la création qui suit se fait dans l'onglet des observations réelles, même
// si le volet était resté sur l'onglet IA ou Parties.
watch(observationsOpenRequested, (requested) => {
  if (requested) {
    setObservationsTab("observations");
    activeDrawer.value = DrawerMenuKey.OBSERVATIONS;
    consumeObservationsOpenRequest();
  }
});

// Ouverture de l'onglet IA demandée ailleurs (fin d'une analyse) : le volet
// Observations s'ouvre, sur cet onglet.
watch(
  () => planAnalysisState.openRequested,
  (requested) => {
    if (requested) {
      setObservationsTab("ai");
      activeDrawer.value = DrawerMenuKey.OBSERVATIONS;
      planAnalysisState.consumeOpenRequest();
    }
  },
);
</script>

<template>
  <!-- Onglets en bulles flottantes, poussées par le volet ouvert -->
  <DrawerTabBubbles
    v-if="!fullscreen"
    :tabs="drawerTabs"
    :active-key="activeDrawer"
    :resizing="isResizing"
    @toggle="toggleDrawer($event as DrawerMenuKey)"
  />

  <transition name="drawer">
    <div
      v-if="!fullscreen && !!activeDrawer"
      class="capture drawer-transition canvas-drawer-panel position-absolute no-capture shadow-3 canvas-text-scaled"
      :class="{ 'drawing-active': isCanvasDrawing }"
      :style="{
        width: `${effectiveDrawerWidth}px`,
        top: `${canvasTopForTexts}px`,
        bottom: 0,
        right: 0,
        zIndex: Z_INDEX_DRAWER,
      }"
    >
      <!-- Poignée de redimensionnement, toujours visible sur le bord gauche -->
      <DrawerResizeHandle @resizing="isResizing = $event" />
      <DRBtnIcon
        variant="flat-white"
        :size="30"
        phosphor-icon="caret-double-right"
        class="drawer-close-btn position-absolute top-0 start-0 m-2"
        @click="handleCloseDrawer()"
      />
      <template v-for="menu in drawerMenuList" :key="menu.key">
        <component
          v-if="activeDrawer === menu.key"
          :is="menu.component"
          :drawingId="drawingId"
          @close="handleCloseDrawer()"
        />
      </template>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.canvas-drawer-panel {
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
}

// Au-dessus du contenu du volet : la barre de titre des onglets est positionnée
// (ancre de son bouton d'aide) et vient après dans le DOM — sans z-index elle se
// peint par-dessus la flèche et avale ses clics.
.drawer-close-btn {
  z-index: 1;
}

.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.drawer-enter-to,
.drawer-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.drawer-enter-active,
.drawer-leave-active {
  transition:
    transform 0.1s ease,
    opacity 0.1s ease;
}
</style>
